import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { CancellationBanner } from '@/components/billing/CancellationBanner';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription, isScheduledForCancellation } from '@/hooks/useSubscription';
import { Link } from '@tanstack/react-router';
import {
  Building2,
  PiggyBank,
  FileText,
  BarChart3,
  Settings,
} from 'lucide-react';

export function DashboardPage() {
  const { user } = useAuth();
  const { subscription } = useSubscription();

  // Show past due warning banner
  const isPastDue = subscription?.status === 'past_due';
  // Show cancellation scheduled banner
  const isScheduledToCancel = isScheduledForCancellation(subscription ?? null);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      {/* Past due banner */}
      {isPastDue && (
        <div className="bg-sapling-warning-100 px-4 py-3 text-center text-sm text-white">
          <span>Your payment is past due. </span>
          <Link
            to="/app/billing"
            className="underline underline-offset-2 hover:no-underline"
          >
            Update payment method
          </Link>
          <span> to avoid service interruption.</span>
        </div>
      )}

      {/* Scheduled cancellation banner */}
      {isScheduledToCancel && !isPastDue && (
        <div className="container mt-4">
          <CancellationBanner periodEndDate={subscription?.current_period_end ?? null} />
        </div>
      )}

      <main className="flex-1 py-8">
        <div className="container">
          {/* Welcome section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold sm:text-3xl">
                Welcome back{user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name}` : ''}!
              </h1>
              <p className="mt-1 text-muted-foreground">
                Here's your financial overview.
              </p>
            </div>
            {/* Billing button moved to Header user dropdown
            <Button variant="outline" size="sm" onClick={handleManageBilling}>
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </Button>
            */}
          </div>

          {/* Quick actions */}
          <div className="mt-8">
            <h2 className="font-display text-lg font-semibold">Quick Actions</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <QuickActionCard
                icon={Building2}
                title="Businesses"
                description="Manage your businesses"
                to="/app"
                disabled
              />
              <QuickActionCard
                icon={PiggyBank}
                title="Personal Finance"
                description="Track personal spending"
                to="/app/personal/dashboard"
              />
              <QuickActionCard
                icon={FileText}
                title="Import Statements"
                description="Upload bank statements"
                to="/app"
                disabled
              />
              <QuickActionCard
                icon={BarChart3}
                title="Reports"
                description="View financial reports"
                to="/app"
                disabled
              />
              <QuickActionCard
                icon={Settings}
                title="Settings"
                description="Account settings"
                to="/app/settings"
              />
            </div>
          </div>

          {/* Coming soon notice */}
          <div className="mt-12 rounded-card border border-dashed bg-muted/50 p-8 text-center">
            <h3 className="font-display text-lg font-semibold">
              Dashboard Under Construction
            </h3>
            <p className="mt-2 text-muted-foreground">
              We're building out the full dashboard experience. Check back soon!
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

interface QuickActionCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  to: string;
  disabled?: boolean;
}

function QuickActionCard({ icon: Icon, title, description, to, disabled }: QuickActionCardProps) {
  if (disabled) {
    return (
      <div className="flex items-start gap-4 rounded-card border bg-card p-4 opacity-50">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-btn bg-sapling-primary-100/10">
          <Icon className="h-5 w-5 text-sapling-primary-100" />
        </div>
        <div>
          <h3 className="font-display font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      className="group flex items-start gap-4 rounded-card border bg-card p-4 transition-shadow hover:shadow-md"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-btn bg-sapling-primary-100/10">
        <Icon className="h-5 w-5 text-sapling-primary-100" />
      </div>
      <div>
        <h3 className="font-display font-semibold group-hover:text-sapling-primary-100">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}
